import type React from "react"
import { memo, useCallback, useState } from "react"
import { t } from "@lingui/core/macro"
import { toast } from "react-toastify"
import { useAppContext } from "../../../../hooks/appContext"
import { useCountdownTimer } from "../../../../hooks/useCountDownTimer"
import { Sizes, Weights } from "../../../../interfaces/sizing"
import {
    useMakeOneClickPurchaseMutation,
    useUpdateAutoRefilSettingsMutation,
} from "../../../../store/purchasePage/purchasePageSlice"
import { getUTCDateFromToday } from "../../../../utils/dateUtils"
import { addPageAction } from "../../../../utils/newrelic"
import { showToast, ToastTypes } from "../../../../utils/sharedToast"
import Button, { ButtonColor } from "../../atoms/Button"
import { NotificationBell, TokensStack } from "../../atoms/Icons/Others"
import { Link } from "../../atoms/Link"
import { Typography } from "../../atoms/Typography"
import "./PreAutorefillToast.scss"
import { handleOpenPushMenuMobile } from "./util"

const PRE_AUTOFILL_TOAST_TIMEOUT = 10

interface OneClickDetailsType {
    subId: string
    productCode: string
}
interface PreAutorefillToastProps {
    oneClickDetails: OneClickDetailsType
    closeToast?: () => void
}

interface PreAutorefillToastMainProps {
    oneClickDetails: OneClickDetailsType
    closeToast?: () => void
    setStep: (step: number) => void
}
const PreAutorefillToastMain = ({
    closeToast,
    setStep,
    oneClickDetails,
}: PreAutorefillToastMainProps) => {
    const [oneClickPurchase, { isLoading }] = useMakeOneClickPurchaseMutation()
    const userName = useAppContext()?.context?.logged_in_user?.username
    const isMobile = useAppContext()?.context?.is_mobile

    const processAutoRefill = useCallback(
        (type: "timeout" | "manual" | "hide") => {
            addPageAction("PreAutorefillToastRefill", {
                type,
            })
            void oneClickPurchase({
                cvv2: "",
                subId: oneClickDetails.subId,
                productId: oneClickDetails.productCode,
                is_autorefill: true,
            })
                .unwrap()
                .then((res) => {
                    if (res.success === false) {
                        showToast({
                            message: t`Auto refill is not available.`,
                            icon: false,
                        })
                    }
                })
                .catch(() => {
                    showToast({
                        message: t`Auto refill is not available.`,
                        icon: false,
                    })
                })
                .finally(() => {
                    if (
                        toast.isActive(ToastTypes.PreAutorefill) &&
                        type !== "hide"
                    ) {
                        closeToast?.()
                    }
                })
        },
        [],
    )

    const handleManageAutoRefillClick = (e: React.MouseEvent) => {
        addPageAction("PreAutorefillToastManage")
        if (isMobile) {
            e.preventDefault()
            handleOpenPushMenuMobile()
        }
    }

    const { timeLeft } = useCountdownTimer({
        initialTime: PRE_AUTOFILL_TOAST_TIMEOUT,
        onComplete: () => {
            processAutoRefill("timeout")
        },
        skipMin: true,
    })

    return (
        <div className="PreAutorefillToast__main">
            <div className="PreAutorefillToast__left">
                <div className="PreAutorefillToast__message">
                    <TokensStack />
                    <div className="PreAutorefillToast__message--content">
                        <Typography
                            size={Sizes.smpx}
                        >{t`Reload tokens automatically in`}</Typography>
                        <Typography
                            className="PreAutorefillToast__timeLeft"
                            weight={Weights.bold}
                            size={Sizes.smpx}
                        >
                            {`${timeLeft} ${t`seconds`}`}
                        </Typography>
                    </div>
                </div>
                <div>
                    {" "}
                    <Button
                        className="PreAutorefillToast__refillNowButton"
                        text={isLoading ? t`Refilling...` : t`Refill Now`}
                        color={ButtonColor.Blue}
                        onClick={() => processAutoRefill("manual")}
                        disabled={isLoading}
                    />
                </div>
            </div>
            <div className="PreAutorefillToast__right">
                <Link
                    target="_blank"
                    href={`/p/${userName}/?tab=settings&from_autorefill_link=1`}
                    onClick={handleManageAutoRefillClick}
                >
                    {t`Manage Auto Refill`}
                </Link>
                <Button
                    className="PreAutorefillToast__hideButton"
                    text={t`Hide`}
                    color={ButtonColor.Link}
                    onClick={() => {
                        setStep(1)
                        addPageAction("PreAutorefillToastHide")
                        setTimeout(
                            () => {
                                processAutoRefill("hide")
                            },
                            (timeLeft as number) * 1000,
                        )
                    }}
                />
            </div>
        </div>
    )
}

// needed not to re trigger timer on re render
const MemoizedPreAutorefillToastMain = memo(PreAutorefillToastMain)
interface PreAutorefillToastForgetProps {
    closeToast?: () => void
}
const PreAutorefillToastForget = ({
    closeToast,
}: PreAutorefillToastForgetProps) => {
    const [updateAutoRefillSettings] = useUpdateAutoRefilSettingsMutation()

    const handleForget = (toDate: string) => {
        void updateAutoRefillSettings({ notifications_timeout: toDate })
            .unwrap()
            .then(() => {
                if (closeToast) {
                    closeToast()
                }
            })
    }
    return (
        <div className="PreAutorefillToast__forget">
            <Typography
                className="PreAutorefillToast__message"
                size={Sizes.smpx}
            >
                <NotificationBell />
                {t`Don't remind me`}
            </Typography>
            <div className="PreAutorefillToast__forgetButtons">
                <Button
                    color={ButtonColor.Blue}
                    text={t`for 7 days`}
                    onClick={() => {
                        addPageAction("PreAutorefillToastForget", {
                            type: "7 days",
                        })
                        handleForget(getUTCDateFromToday(7))
                    }}
                />
                <Button
                    color={ButtonColor.Blue}
                    text={t`for 14 days`}
                    onClick={() => {
                        addPageAction("PreAutorefillToastForget", {
                            type: "14 days",
                        })
                        handleForget(getUTCDateFromToday(14))
                    }}
                />
                <Button
                    color={ButtonColor.Blue}
                    text={t`forever`}
                    onClick={() => {
                        addPageAction("PreAutorefillToastForget", {
                            type: "forever",
                        })
                        handleForget("forever")
                    }}
                />
            </div>
        </div>
    )
}

export const PreAutorefillToast = ({
    oneClickDetails,
    closeToast,
}: PreAutorefillToastProps) => {
    const [step, setStep] = useState(0)

    const handleStepChange = useCallback((newStep: number) => {
        setStep(newStep)
    }, [])

    return (
        <div className="PreAutorefillToast">
            {step === 0 && (
                <MemoizedPreAutorefillToastMain
                    setStep={handleStepChange}
                    closeToast={closeToast}
                    oneClickDetails={oneClickDetails}
                />
            )}
            {step === 1 && <PreAutorefillToastForget closeToast={closeToast} />}
        </div>
    )
}
